/**
 * Toast d'invitation à rejoindre une table brouillon.
 * 
 * Comportement selon le profil de disponibilité :
 * - bougon    : jamais affiché (géré côté backend)
 * - timide    : boutons Accepter/Refuser, auto-decline au timeout
 * - detendu   : boutons Accepter/Refuser, auto-accept au timeout
 * - enthousiaste / on_fire : pas de boutons, auto-accept, message "vous allez rejoindre..."
 * 
 * La réponse n'est envoyée qu'à la fin du délai de 10s (confidentialité).
 * Le hook useNotificationSocket gère l'envoi différé ; ce composant ne fait
 * que l'affichage et enregistre la décision de l'utilisateur.
 */

import { useState, useEffect, useCallback } from 'react';
import { PlayerAvatar } from './PlayerAvatar';
import type { InvitationData } from '../hooks/useNotificationSocket';
import type { AvailabilityProfile } from '../types/draft';

const TIMEOUT_SECONDS = 10;

type ToastPhase = 'choosing' | 'accepted';

interface InvitationToastProps {
  invitation: InvitationData;
  /** Records the decision (not sent immediately — hook handles the delay) */
  onRespond: (draftId: string, accepted: boolean) => void;
  /** Removes the toast from the visible list */
  onDismiss: (draftId: string) => void;
}

function hasButtons(profile: AvailabilityProfile): boolean {
  return profile === 'timide' || profile === 'detendu';
}

export default function InvitationToast({
  invitation,
  onRespond,
  onDismiss,
}: InvitationToastProps) {
  const { availability } = invitation;
  const showButtons = hasButtons(availability);

  const [phase, setPhase] = useState<ToastPhase>(
    showButtons ? 'choosing' : 'accepted',
  );
  const [startAnimation, setStartAnimation] = useState(false);

  // Kick off the CSS transition one frame after mount
  useEffect(() => {
    requestAnimationFrame(() => {
      requestAnimationFrame(() => setStartAnimation(true));
    });
  }, []);

  const handleAccept = useCallback(() => {
    onRespond(invitation.draftId, true);
    setPhase('accepted');
  }, [invitation.draftId, onRespond]);

  const handleDecline = useCallback(() => {
    onRespond(invitation.draftId, false);
    // Toast disappears immediately on explicit decline
    onDismiss(invitation.draftId);
  }, [invitation.draftId, onRespond, onDismiss]);

  const modeLabel = invitation.config.gameMode === 'defi_libre'
    ? 'Défi libre' : 'Table libre';

  const showPlayerCount = !invitation.compet;

  return (
    <div className="invitation-toast">
      {/* Progress bar — shrinks from 100% to 0% over TIMEOUT_SECONDS */}
      <div className="invitation-progress-track">
        <div
          className={`invitation-progress-bar ${startAnimation ? 'animating' : ''}`}
          style={{ transitionDuration: `${TIMEOUT_SECONDS}s` }}
        />
      </div>

      <div className="invitation-toast-content">
        <div className="invitation-toast-icon">🎴</div>
        <div className="invitation-toast-info">
          <div className="invitation-toast-title">Invitation à jouer</div>
          <div className="invitation-toast-details">
            <strong>{invitation.fromPseudoDisplay}</strong> vous invite
            {' '}— {modeLabel}
            {showPlayerCount && ` (${invitation.config.nombreJoueurs} joueurs)`}
          </div>

          {/* Players already at the table */}
          {invitation.players.length > 0 && (
            <div className="invitation-toast-players">
              {invitation.players.map(p => (
                <div key={p.pseudo} className="invitation-toast-player">
                  <PlayerAvatar pseudo={p.pseudo} size={20} />
                  <span>{p.pseudoDisplay}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Phase-dependent content */}
      {phase === 'choosing' && (
        <>
          <div className="invitation-toast-hint">
            {availability === 'timide'
              ? 'Sans réponse, l\'invitation sera refusée'
              : 'Sans réponse, l\'invitation sera acceptée'}
          </div>
          <div className="invitation-toast-actions">
            <button className="invitation-accept-btn" onClick={handleAccept}>
              Accepter
            </button>
            <button className="invitation-decline-btn" onClick={handleDecline}>
              Refuser
            </button>
          </div>
        </>
      )}

      {phase === 'accepted' && (
        <div className="invitation-toast-confirmed">
          Vous allez rejoindre la partie d'ici quelques secondes…
        </div>
      )}
    </div>
  );
}

/**
 * Container that renders all pending invitation toasts.
 */
interface InvitationToastContainerProps {
  invitations: InvitationData[];
  onRespond: (draftId: string, accepted: boolean) => void;
  onDismiss: (draftId: string) => void;
}

export function InvitationToastContainer({
  invitations,
  onRespond,
  onDismiss,
}: InvitationToastContainerProps) {
  if (invitations.length === 0) return null;

  return (
    <div className="invitation-toast-container">
      {invitations.map(inv => (
        <InvitationToast
          key={inv.draftId}
          invitation={inv}
          onRespond={onRespond}
          onDismiss={onDismiss}
        />
      ))}
    </div>
  );
}
